import type { BookingSessionResponse } from '@massilia/queries'

import { DEFAULT_OPTION, useAppForm } from '@massilia/features'
import {
  useGetMyPassengersQuery,
  useGetMyProfileQuery,
  useSubmitPassengers,
} from '@massilia/queries'
import { Button, cn } from '@massilia/ui'
import { revalidateLogic, standardSchemaValidators } from '@tanstack/react-form'
import { useNavigate } from '@tanstack/react-router'
import { differenceInYears, parse } from 'date-fns'
import { AlertTriangle, ChevronLeft, Info, Loader2, Mail } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import type { InformationsPassengerSchema } from '@/lib/schemas/informations'

import { BookingSidebar } from '@/components/booking/booking-sidebar'
import { BookingStepActions } from '@/components/booking/booking-step-actions'
import { TripHeader } from '@/components/booking/trip-header'
import { createInformationsSchema } from '@/lib/schemas/informations'
import { useAuthStore } from '@/stores/auth.store'
import { useFlightSearchStore } from '@/stores/flight-search.store'

import { getPassengerLabel, getPassengerType, getTotalPassengerCount } from '../-utils/constants'
import { PassengerFieldGroup } from './passenger-field-group'

interface InformationsFormProps {
  sessionId: string
  session?: BookingSessionResponse
}

export function InformationsForm({ sessionId, session }: InformationsFormProps) {
  const navigate = useNavigate()
  const { isAuthenticated, user } = useAuthStore()
  const { lastFlightResultsParams, lastFlightResultsRoute } = useFlightSearchStore()
  const [isNavigating, setIsNavigating] = useState(false)
  const [babyCompanionError, setBabyCompanionError] = useState<null | string>(null)

  const isLocked = !!session?.bookingId

  const { data: savedPassengers } = useGetMyPassengersQuery(isAuthenticated)
  const { data: profile } = useGetMyProfileQuery(isAuthenticated)
  const { isPending: isSubmitting, mutateAsync: submitPassengers } = useSubmitPassengers(
    sessionId,
    { throwOnError: true }
  )

  // Phone is stored on Client.phoneNumbers[] (multiple numbers w/ types). Prefer the
  // PRINCIPAL one, fallback to the first available.
  const accountPhone =
    profile?.client?.phoneNumbers?.find((p) => p.type === 'PRINCIPAL')?.number ??
    profile?.client?.phoneNumbers?.[0]?.number ??
    ''

  const passengersCount = (session?.flightDetails.passengersCount ?? {}) as {
    adults?: number
    babies?: number
    infants?: number
    seniors?: number
    youngsters?: number
  }

  const resolvedPassengersCount = {
    adults: passengersCount.adults ?? 0,
    babies: passengersCount.babies ?? 0,
    infants: passengersCount.infants ?? 0,
    seniors: passengersCount.seniors ?? 0,
    youngsters: passengersCount.youngsters ?? 0,
  }

  const firstJourney = session?.priceData?.journeys?.at(0)
  const firstSegment = firstJourney?.segments?.at(0)
  const firstFlightDepartureDate = firstSegment?.departure?.rawDate ?? ''

  const lastJourney = session?.priceData?.journeys?.at(-1)
  const lastSegment = lastJourney?.segments?.at(-1)
  const lastFlightArrivalDate = lastSegment?.arrival?.rawDate ?? ''

  const defaultPassengers = useMemo(
    () =>
      session?.passengers && session.passengers.length > 0
        ? session.passengers.map((p) => {
            const [year = '', month = '', day = ''] = p.birthDate?.split('-') ?? []
            return {
              firstName: p.firstName,
              lastName: p.lastName,
              savedPassengerId: null,
              birthDay: day,
              birthMonth: month,
              birthYear: year,
              fidelityCard: p.fidelityCard
                ? {
                    companyName: {
                      label: p.fidelityCard.companyName,
                      value: p.fidelityCard.companyName,
                    },
                    cardNumber: p.fidelityCard.cardNumber,
                  }
                : undefined,
              gender: p.gender as 'FEMALE' | 'MALE',
              identityDocument: p.identityDocument
                ? {
                    documentNumber: p.identityDocument.documentNumber,
                    expiryDate: p.identityDocument.expiryDate,
                    issuingCountry: {
                      label: p.identityDocument.issuingCountry,
                      value: p.identityDocument.issuingCountry,
                    },
                    type: p.identityDocument.type,
                  }
                : undefined,
              nationality: p.nationality
                ? { label: p.nationality, value: p.nationality }
                : DEFAULT_OPTION,
              retrieveFromSaved: false,
            }
          })
        : createDefaultPassengers(
            (session?.flightDetails.passengersCount ?? {}) as {
              adults?: number
              babies?: number
              infants?: number
              seniors?: number
              youngsters?: number
            }
          ),
    [session?.passengers, session?.flightDetails.passengersCount]
  )

  const form = useAppForm({
    defaultValues: {
      buyerEmail: session?.buyerEmail ?? user?.email ?? '',
      acceptsDepositCommitment: isLocked,
      acceptsTermsAndConditions: isLocked,
      buyerPhone: session?.buyerPhone ?? accountPhone ?? '',
      passengers: defaultPassengers,
    },
    listeners: {
      onChange: ({ fieldApi }) => {
        if (/^passengers\[\d+\]\.birth(Day|Month|Year)$/.test(fieldApi.name)) {
          setBabyCompanionError(null)
        }
      },
    },
    onSubmit: async ({ value }) => {
      const passengers = value.passengers.map((p, index) => ({
        firstName: p.firstName,
        lastName: p.lastName,
        birthDate: `${p.birthYear}-${p.birthMonth.padStart(2, '0')}-${p.birthDay.padStart(2, '0')}`,
        fidelityCard: p.fidelityCard
          ? {
              companyName: p.fidelityCard.companyName.label,
              cardNumber: p.fidelityCard.cardNumber,
            }
          : undefined,
        gender: p.gender as 'FEMALE' | 'MALE',
        identityDocument: p.identityDocument
          ? {
              documentNumber: p.identityDocument.documentNumber,
              expiryDate: p.identityDocument.expiryDate,
              issuingCountry: p.identityDocument.issuingCountry.value,
              type: p.identityDocument.type,
            }
          : undefined,
        nationality: p.nationality.value,
        type: getPassengerType(resolvedPassengersCount, index),
      }))

      await submitPassengers({
        buyerEmail: value.buyerEmail.trim(),
        buyerPhone: value.buyerPhone.trim(),
        passengers,
      })

      setIsNavigating(true)
      void navigate({
        search: { session: sessionId },
        to: '/assurances',
      })
    },
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'change',
    }),
    validators: {
      onDynamic: ({ value }) => {
        const schema = createInformationsSchema({
          firstFlightDepartureDate,
          lastFlightArrivalDate,
          passengersCount: resolvedPassengersCount,
        })
        return standardSchemaValidators.validate({ validationSource: 'form', value }, schema)
      },
    },
  })

  // Profile fetch is async — if it arrives after the form has mounted with an empty
  // phone, hydrate the field. Don't overwrite a value the user has already typed
  // or one already saved on the session.
  useEffect(() => {
    if (!accountPhone) return
    if (session?.buyerPhone) return
    if (form.state.values.buyerPhone) return
    form.setFieldValue('buyerPhone', accountPhone)
  }, [accountPhone, session?.buyerPhone, form])

  return (
    <form
      className="flex w-full flex-col gap-8 px-4 py-8 md:px-28 lg:flex-row lg:gap-12"
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()

        if (isLocked) {
          setIsNavigating(true)
          void navigate({ search: { session: sessionId }, to: '/assurances' })
          return
        }

        // Check baby companion constraint for banner display
        const passengers = form.state.values.passengers
        let nbBabies = 0
        let nbAdults18Plus = 0
        const departureDate = parse(firstFlightDepartureDate, 'yyyy-MM-dd', new Date())
        for (const p of passengers) {
          if (p.birthYear && p.birthMonth && p.birthDay) {
            const birthDate = parse(
              `${p.birthYear}-${p.birthMonth}-${p.birthDay}`,
              'yyyy-MM-dd',
              new Date()
            )
            const age = differenceInYears(departureDate, birthDate)
            if (age < 2) nbBabies++
            if (age >= 18) nbAdults18Plus++
          }
        }
        if (nbBabies > 0 && nbBabies > nbAdults18Plus) {
          setBabyCompanionError(
            `Chaque bébé doit être accompagné par un adulte de 18 ans ou plus. Il y a ${nbBabies} bébé(s) pour ${nbAdults18Plus} adulte(s) éligible(s).`
          )
          return
        }

        setBabyCompanionError(null)
        void form.handleSubmit()
      }}
    >
      {/* Mobile-only TripHeader */}
      {session && (
        <div className="lg:hidden">
          <TripHeader session={session} />
        </div>
      )}

      {/* Right column — Sidebar */}
      <div className="w-full shrink-0 lg:order-last lg:w-96">
        <BookingSidebar isLoading={!session?.priceData} session={session} />
      </div>

      {/* Left column — Form */}
      <div className="flex flex-1 flex-col gap-9">
        {/* Desktop-only TripHeader */}
        {session && (
          <div className="hidden lg:block">
            <TripHeader session={session} />
          </div>
        )}

        {/* Lock banner shown after the booking has been registered with Affidium */}
        {isLocked && (
          <div className="border-brand-100 bg-brand-50 flex items-start gap-3 rounded-lg border p-3">
            <Info className="text-brand-600 mt-0.5 size-4 shrink-0" />
            <p className="text-gray-light-900 text-sm">
              Votre réservation a été enregistrée. Pour modifier les informations passagers ou les
              coordonnées, contactez l&apos;agence.
            </p>
          </div>
        )}

        {/* Section: Vos coordonnées */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <h2 className="text-gray-light-900 text-lg font-semibold">Vos coordonnées</h2>
            <p className="text-gray-light-900 text-sm">
              Ces informations nous permettent de finaliser votre réservation et de vous contacter.
            </p>
          </div>
          <div className="flex flex-col gap-4 md:flex-row md:items-end">
            <div className="flex-1">
              <form.AppField name="buyerEmail">
                {(field) => (
                  <field.TextField
                    icon={Mail}
                    label="Adresse email"
                    placeholder="votre@email.com"
                    disabled={isLocked}
                    type="email"
                  />
                )}
              </form.AppField>
            </div>
            <div className="flex-1">
              <form.AppField name="buyerPhone">
                {(field) => <field.PhoneNumberField label="Téléphone" disabled={isLocked} />}
              </form.AppField>
            </div>
          </div>
        </div>

        {/* Section: Qui sont les voyageurs ? */}
        <div className="flex flex-col gap-2">
          <h2 className="text-gray-light-900 text-lg font-semibold">Qui sont les voyageurs ?</h2>
          <p className="text-gray-light-900 text-sm">
            Les noms des voyageurs doivent être identiques à ceux indiqués sur vos cartes
            d&apos;identité ou passeports
          </p>
        </div>

        {/* Baby companion error banner */}
        {babyCompanionError && (
          <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-3">
            <AlertTriangle className="mt-0.5 size-4 shrink-0 text-red-600" />
            <p className="text-sm text-red-800">{babyCompanionError}</p>
          </div>
        )}

        {/* Passenger field groups — fully disabled once the booking is locked */}
        <fieldset
          className={cn('flex flex-col gap-9', isLocked && 'pointer-events-none opacity-60')}
          disabled={isLocked}
        >
          <form.AppField mode="array" name="passengers">
            {(passengersField) =>
              passengersField.state.value.map((_, index) => (
                <PassengerFieldGroup
                fields={{
                  firstName: `passengers[${index}].firstName`,
                  lastName: `passengers[${index}].lastName`,
                  savedPassengerId: `passengers[${index}].savedPassengerId`,
                  birthDay: `passengers[${index}].birthDay`,
                  birthMonth: `passengers[${index}].birthMonth`,
                  birthYear: `passengers[${index}].birthYear`,
                  fidelityCard: `passengers[${index}].fidelityCard`,
                  gender: `passengers[${index}].gender`,
                  identityDocument: `passengers[${index}].identityDocument`,
                  nationality: `passengers[${index}].nationality`,
                  retrieveFromSaved: `passengers[${index}].retrieveFromSaved`,
                }}
                form={form}
                index={index}
                key={index}
                label={getPassengerLabel(resolvedPassengersCount, index)}
                savedPassengers={savedPassengers}
              />
              ))
            }
          </form.AppField>
        </fieldset>

        {/* Consentements (CGV + engagement acompte) */}
        <div className="flex flex-col gap-3">
          <form.AppField name="acceptsTermsAndConditions">
            {(field) => (
              <field.CheckboxField
                containerClassName="items-start"
                disabled={isLocked}
                label={
                  <span>
                    J&apos;accepte les{' '}
                    <a
                      className="text-brand-700 font-medium underline"
                      href="https://www.massiliavoyages.fr/conditions-generales-vente-vols-secs/"
                      rel="noreferrer"
                      target="_blank"
                    >
                      conditions générales de ventes
                    </a>
                  </span>
                }
              />
            )}
          </form.AppField>
          <form.AppField name="acceptsDepositCommitment">
            {(field) => (
              <field.CheckboxField
                containerClassName="items-start"
                disabled={isLocked}
                label="Je souhaite réserver et m'engage à verser l'acompte ou la totalité du billet dans les délais indiqués dans la prochaine étape"
              />
            )}
          </form.AppField>
        </div>

        {/* Navigation buttons */}
        <BookingStepActions>
          <Button
            className="gap-2"
            disabled={isSubmitting || isNavigating}
            onClick={() => {
              if (lastFlightResultsParams) {
                void navigate({
                  search: lastFlightResultsParams,
                  to: (lastFlightResultsRoute ?? '/vols/resultats') as string,
                })
              } else {
                void navigate({ to: '/recherche' })
              }
            }}
            type="button"
            variant="ghost"
          >
            <ChevronLeft className="text-brand-700 size-4" />
            <span className="text-brand-700 text-sm font-medium">Étape précédente</span>
          </Button>
          <Button
            className="bg-brand-600 hover:bg-brand-700 w-56 gap-2"
            disabled={isSubmitting || isNavigating}
            type="submit"
          >
            {isSubmitting || isNavigating ? (
              <span className="flex items-center gap-2 text-sm font-medium text-white">
                <Loader2 className="size-4 animate-spin text-white" />
                {isLocked ? null : 'Réservation en cours…'}
              </span>
            ) : (
              <span className="text-sm font-medium text-white">
                {isLocked ? 'Continuer' : 'Je réserve'}
              </span>
            )}
          </Button>
        </BookingStepActions>
      </div>
    </form>
  )
}

function createDefaultPassengers(
  passengers: Record<string, number | undefined>
): InformationsPassengerSchema[] {
  const total = getTotalPassengerCount({
    adults: passengers.adults ?? 0,
    babies: passengers.babies ?? 0,
    infants: passengers.infants ?? 0,
    seniors: passengers.seniors ?? 0,
    youngsters: passengers.youngsters ?? 0,
  })
  return Array.from({ length: total }, () => ({
    firstName: '',
    lastName: '',
    savedPassengerId: null,
    birthDay: '',
    birthMonth: '',
    birthYear: '',
    fidelityCard: undefined,
    gender: '' as 'FEMALE' | 'MALE',
    identityDocument: undefined,
    nationality: DEFAULT_OPTION,
    retrieveFromSaved: false,
  }))
}
